import { useEffect, useMemo, useState } from 'react';
import { Map as ImmutableMap } from 'immutable';
import AsyncData from 'conversations-async-data/async-data/AsyncData';
import userInfo from 'hub-http/userInfo';
import CallNumbersPopover from './CallNumbersPopover';
import CallWorkspacePopover from './CallWorkspacePopover';
import CallNumbersActivityCreatorPopover from './CallNumbersActivityCreatorPopover';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { isFailed, isLoading, isUninitialized } from 'conversations-async-data/async-data/operators/statusComparators';
import { FAILED, SUCCEEDED } from 'conversations-async-data/async-data/constants/asyncStatuses';
import { logNetworkError } from 'calling-lifecycle-internal/utils/logNetworkError';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import CalleeError from './callee-error/CalleeError';
import { MOVE_INBOX_TO_CALLTAB } from 'calling-internal-common/constants/CallingGates';
import { PROFILE, HELP_DESK, INBOX, REFERENCE_UI, ACTIVITY_CREATOR } from 'calling-internal-common/constants/appIdentifiers';
import { jsx as _jsx } from "react/jsx-runtime";
function getAppIdentifierFromUrl() {
  const url = window.location.pathname;
  return url.includes('/record/') ? PROFILE : url.includes('/help-desk/') ? HELP_DESK : url.includes('/inbox/') ? INBOX : url.includes('/cv-calling-integration/') ? REFERENCE_UI : ACTIVITY_CREATOR;
}
function isHelpdeskComponent(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === HELP_DESK;
}
function isInboxComponent(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === INBOX;
}
function isActivityCreatorComponent(appIdentifierFromUrl, props) {
  return appIdentifierFromUrl === ACTIVITY_CREATOR;
}
const useCallNumbersPopover = props => {
  const appIdentifierFromUrl = getAppIdentifierFromUrl();
  const [auth, setAuth] = useState(new AsyncData());
  const {
    objectTypeId,
    onCallButtonClick,
    shouldFetch,
    subjectId,
    visitorPhoneNumber,
    isKnownVisitor
  } = props;
  useEffect(() => {
    // Only fetch if popover is open and auth has not been initialized
    if (!shouldFetch || !isUninitialized(auth)) {
      return;
    }
    userInfo().then(data => {
      setAuth(new AsyncData({
        status: SUCCEEDED,
        data
      }));
    }).catch(networkError => {
      setAuth(new AsyncData({
        status: FAILED,
        error: networkError
      }));
      logNetworkError({
        error: new Error('Failed to fetch user info'),
        networkError
      });
    });
  }, [shouldFetch, auth]);
  const renderedCallNumbersPopover = useMemo(() => {
    if (isLoading(auth)) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered",
        minHeight: 100,
        "data-test-id": "call-numbers-popover-loading-spinner"
      });
    }
    if (isFailed(auth)) {
      return /*#__PURE__*/_jsx(CalleeError, {
        "data-test-id": "call-numbers-popover-callee-error"
      });
    }
    const {
      user,
      portal,
      gates
    } = getData(auth);
    // Convert scopes to ImmutableMap friendly (ie. ['scope', true])
    const scopes = user.scopes.reduce((acc, scope) => {
      acc[scope] = true;
      return acc;
    }, {});
    const isHelpdesk = isHelpdeskComponent(appIdentifierFromUrl, props);
    const isInbox = isInboxComponent(appIdentifierFromUrl, props);
    const isActivityCreator = isActivityCreatorComponent(appIdentifierFromUrl, props);
    const inboxIsUngatedForCallTab = gates.includes(MOVE_INBOX_TO_CALLTAB);
    return isActivityCreator ? /*#__PURE__*/_jsx(CallNumbersActivityCreatorPopover, {
      appIdentifier: appIdentifierFromUrl,
      limits: ImmutableMap(portal.limits),
      objectTypeId: objectTypeId,
      onCallButtonClick: onCallButtonClick,
      "data-test-id": "call-numbers-popover",
      scopes: ImmutableMap(scopes),
      subjectId: subjectId
    }) : isHelpdesk || isInbox && inboxIsUngatedForCallTab ? /*#__PURE__*/_jsx(CallWorkspacePopover, {
      appIdentifier: appIdentifierFromUrl,
      "data-test-id": "call-numbers-popover",
      gates: gates,
      inboxId: props.inboxId,
      limits: ImmutableMap(portal.limits),
      objectTypeId: objectTypeId,
      onCallButtonClick: onCallButtonClick,
      scopes: ImmutableMap(scopes),
      subjectId: subjectId,
      defaultFromNumber: props.defaultFromNumber,
      visitorPhoneNumber: visitorPhoneNumber,
      isKnownVisitor: isKnownVisitor
    }) : /*#__PURE__*/_jsx(CallNumbersPopover, {
      subjectId: subjectId,
      objectTypeId: objectTypeId,
      onCallButtonClick: onCallButtonClick,
      scopes: ImmutableMap(scopes),
      limits: ImmutableMap(portal.limits),
      gates: gates,
      appIdentifier: appIdentifierFromUrl,
      "data-test-id": "call-numbers-popover"
    });
  }, [appIdentifierFromUrl, auth, isKnownVisitor, objectTypeId, onCallButtonClick, props, subjectId, visitorPhoneNumber]);
  return [renderedCallNumbersPopover];
};
export default useCallNumbersPopover;